/**
 * 定期請求 /recurring（#268 1.5a、docs/98、Figma「定期請求（10/29）」② 283:54）。一覧の型（ListHead）。締めの画面（#267）と同じ形。
 *   題の行：‹ 請求書一覧｜定期請求（？）｜月の切替（‹ 2026年11月分 ›）
 *   表のカード：タブ（すべて／未作成／下書き／発行済）・絞り込み（{customer}・契約）・選んだときの帯（n件 選択・合計（税抜）・選択解除・今月分作成）
 *   行＝契約：{customer}／契約・請求日・間隔と次の請求月・状態（継続／停止／終了）・テンプレートの合計（税抜）・その月の状態・「下書き作成」か請求書へのリンク・⋯（停止／再開／終了）
 *   表の下：n契約・月額の合計（税抜）・その月の 未作成・下書き・発行済 の数
 * 左のメニューに項目は足さない（入口は請求書の一覧の「定期請求」とホームの「定期 n件（下書き）」。印は請求書＝AreaIcon の areaOfPath）
 */
import {
  RECURRING_MONTH_STATE_LABEL,
  RECURRING_MSG,
  RECURRING_STATUS_LABEL,
  closingMonthLabel,
  recurringDayLabel,
  recurringIntervalLabel,
  shiftMonth,
  type GroupRecurring,
  type RecurringCreateResult,
  type RecurringMonthState,
  type RecurringOverview,
  type RecurringRow,
  type RecurringStatus,
} from "@genbaryoku/contract";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { t } from "../../labels.js";
import { todayJst, yen } from "../../lib/num.js";

type Tab = "all" | Exclude<RecurringMonthState, "skip">;
const TABS: Tab[] = ["all", "none", "draft", "issued"];
const TAB_LABEL: Record<Tab, string> = { all: "すべて", none: RECURRING_MONTH_STATE_LABEL.none, draft: RECURRING_MONTH_STATE_LABEL.draft, issued: RECURRING_MONTH_STATE_LABEL.issued };
/** その月の状態のしるし：未作成＝枠／下書き＝薄い枠／発行済＝主色の淡い地／対象外＝文字だけ */
const MONTH_CHIP: Record<RecurringMonthState, string> = { none: "chip", draft: "chip quiet", issued: "chip ai", skip: "rc-skip" };
const STATE_CHIP: Record<RecurringStatus, string> = { active: "chip ai", paused: "chip", ended: "chip quiet" };
const mOf = (month: string) => Number(month.slice(5, 7));

export function RecurringPage() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const monthQ = sp.get("month") ?? "";
  usePageName(RECURRING_MSG.title);
  const ov = useQuery({
    queryKey: ["recurring", monthQ],
    queryFn: () => api<RecurringOverview>("GET", `/recurring${monthQ ? `?month=${monthQ}` : ""}`),
    placeholderData: keepPreviousData,
  });
  // 月の切替は答えを待たずに描く（#179：あとから差し込まない。日本時間の今月＝API の thisMonth と同じ）
  const jstMonth = todayJst().slice(0, 7);
  const month = ov.data?.month ?? (monthQ || jstMonth);
  const thisMonth = ov.data?.thisMonth ?? jstMonth;
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const { toast, node: toastNode } = useToast();

  const setMonth = (m: string) => {
    setPicked([]);
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set("month", m);
      return n;
    });
  };
  const rows = ov.data?.rows ?? [];
  const word = q.trim();
  const hit = (r: RecurringRow) => !word || r.client.name.includes(word) || r.group.name.includes(word);
  const pick = (k: Tab) => rows.filter((r) => (k === "all" || r.monthState === k) && hit(r));
  const items = pick(tab);
  const pickedRows = rows.filter((r) => picked.includes(r.planId) && r.monthState === "none");
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["recurring"] });
    void qc.invalidateQueries({ queryKey: ["group-recurring"] });
    void qc.invalidateQueries({ queryKey: ["invoices"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const create = async (ids: string[]) => {
    if (busy || !ids.length || !month) return;
    setBusy(true);
    try {
      const r = await api<RecurringCreateResult>("POST", "/recurring/create-month", { month, planIds: ids });
      refresh();
      setPicked([]);
      for (const f of r.failed) toast(`${f.name}：${f.message}`, undefined, "error", "recurring-failed");
      if (r.created.length === 1 && ids.length === 1) {
        nav(`/invoices/${r.created[0]!.invoiceId}`, { state: { notice: RECURRING_MSG.createdOne } });
        return;
      }
      if (r.created.length) toast(RECURRING_MSG.created(r.created.length), undefined, "success", "recurring-created");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };
  const setState = async (r: RecurringRow, path: "pause" | "resume" | "end") => {
    try {
      await api<GroupRecurring>("POST", `/recurring/${r.planId}/${path}`);
      refresh();
      toast(path === "pause" ? RECURRING_MSG.paused : path === "resume" ? RECURRING_MSG.resumed : RECURRING_MSG.ended, undefined, "success", "recurring-state");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };

  const search = <FilterInput value={q} onChange={setQ} placeholder={t(RECURRING_MSG.filter)} testId="recurring-search" />;
  const cols = 9;
  const counts = (s: RecurringMonthState) => rows.filter((r) => r.monthState === s).length;
  return (
    <ListHead
      back={<BackButton fallback={{ to: "/invoices", label: RECURRING_MSG.back }} />}
      title={
        <>
          {RECURRING_MSG.title}
          <Help testId="help-recurring" label={RECURRING_MSG.title} text={t(RECURRING_MSG.help)} />
        </>
      }
      titleTestId="recurring-title"
      extra={
        month ? (
          <span className="cl-month" role="group" aria-label="月の切替" data-testid="recurring-month">
            <button type="button" className="btn sm quiet" aria-label="前の月" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="recurring-month-prev">
              ‹
            </button>
            <b className="num" data-testid="recurring-month-label">{closingMonthLabel(month)}</b>
            <button type="button" className="btn sm quiet" aria-label="次の月" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="recurring-month-next">
              ›
            </button>
          </span>
        ) : null
      }
    >
      {ov.isError ? <ErrorText>{messageOf(ov.error)}</ErrorText> : null}
      <div className="lcard">
        <ListTabs id="rc" label="定期請求をその月の状態で絞る" tabs={TABS.map((k) => ({ key: k, label: TAB_LABEL[k], count: ov.data ? pick(k).length : "…" }))} value={tab} onChange={(k) => setTab(k as Tab)} filter={search} />
        <div className="tbl-wrap ltab-panel" {...listTabPanel("rc", tab)}>
          {pickedRows.length ? (
            <div className="cl-bulk" data-testid="recurring-bulk">
              <b>{RECURRING_MSG.selected(pickedRows.length)}</b>
              <span>
                合計（税抜） <b className="num">{yen(pickedRows.reduce((a, r) => a + r.subtotal, 0))}</b> 円
              </span>
              <span className="sp" />
              <button type="button" className="btn text" onClick={() => setPicked([])} data-testid="recurring-deselect">
                {RECURRING_MSG.deselect}
              </button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void create(pickedRows.map((r) => r.planId))} data-testid="recurring-create-all">
                {RECURRING_MSG.createAll(mOf(month), month === thisMonth)}
              </button>
            </div>
          ) : null}
          <table className="list cl-list rc-list" data-testid="recurring-list">
            <thead>
              <tr>
                <th className="pick">
                  {items.some((r) => r.monthState === "none") ? (
                    <input
                      type="checkbox"
                      aria-label="未作成の契約をすべて選ぶ"
                      checked={items.filter((r) => r.monthState === "none").every((r) => picked.includes(r.planId))}
                      onChange={(e) => {
                        const ids = items.filter((r) => r.monthState === "none").map((r) => r.planId);
                        setPicked((p) => (e.target.checked ? [...new Set([...p, ...ids])] : p.filter((x) => !ids.includes(x))));
                      }}
                      data-testid="recurring-pick-all"
                    />
                  ) : null}
                </th>
                <th>{t(RECURRING_MSG.colClient)}</th>
                <th>{RECURRING_MSG.colDay}</th>
                <th>{RECURRING_MSG.colInterval}</th>
                <th>{RECURRING_MSG.colState}</th>
                <th className="amt">{RECURRING_MSG.colSubtotal}</th>
                <th>{month ? RECURRING_MSG.colMonth(mOf(month)) : ""}</th>
                <th />
                <th className="more" aria-label="操作" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.planId} className="r" data-testid="recurring-row" data-client={r.client.name} onClick={() => nav(`/groups/${r.group.id}?tab=recurring`)}>
                  <td className="pick" onClick={(e) => e.stopPropagation()}>
                    {r.monthState === "none" ? (
                      <input
                        type="checkbox"
                        aria-label={`${r.client.name} ${r.group.name} を選ぶ`}
                        checked={picked.includes(r.planId)}
                        onChange={(e) => setPicked((p) => (e.target.checked ? [...p, r.planId] : p.filter((y) => y !== r.planId)))}
                        data-testid={`recurring-pick-${r.group.name}`}
                      />
                    ) : null}
                  </td>
                  <td>
                    <b>{r.client.name}</b>
                    <div className="muted sm">{r.group.name}</div>
                  </td>
                  <td>{recurringDayLabel(r.dayRule, r.dayOfMonth)}</td>
                  <td>
                    {recurringIntervalLabel(r.intervalMonths)}
                    {r.nextMonth ? `・${RECURRING_MSG.next(mOf(r.nextMonth))}` : ""}
                  </td>
                  <td>
                    <span className={STATE_CHIP[r.state]} data-testid="recurring-state">{RECURRING_STATUS_LABEL[r.state]}</span>
                  </td>
                  <td className="amt num">
                    <b>{yen(r.subtotal)}</b>
                  </td>
                  <td>
                    <span className={MONTH_CHIP[r.monthState]} data-testid="recurring-month-state">{RECURRING_MONTH_STATE_LABEL[r.monthState]}</span>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {r.invoice ? (
                      <Link to={`/invoices/${r.invoice.id}`} className="rc-inv" data-testid="recurring-invoice">{r.invoice.documentNo ?? RECURRING_MSG.invoiceLink} ›</Link>
                    ) : r.monthState === "none" ? (
                      <button type="button" className="btn" disabled={busy} onClick={() => void create([r.planId])} data-testid={`recurring-create-${r.group.name}`}>
                        {RECURRING_MSG.create}
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="more" onClick={(e) => e.stopPropagation()}>
                    <RowMenu
                      label={`${r.client.name} ${r.group.name} ${RECURRING_MSG.more}`}
                      testId={`recurring-more-${r.group.name}`}
                      items={[
                        ...(r.state === "active" ? [{ label: RECURRING_MSG.pause, onSelect: () => void setState(r, "pause"), testId: "recurring-pause" }] : [{ label: RECURRING_MSG.resume, onSelect: () => void setState(r, "resume"), testId: "recurring-resume" }]),
                        ...(r.state !== "ended" ? [{ label: RECURRING_MSG.end, onSelect: () => void setState(r, "end"), testId: "recurring-end" }] : []),
                      ]}
                    />
                  </td>
                </tr>
              ))}
              {ov.data && !items.length ? (
                <tr className="emptyrows">
                  <td colSpan={cols} data-testid="recurring-empty">
                    {RECURRING_MSG.empty}
                    <div className="muted sm">{t(RECURRING_MSG.emptyHelp)}</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {rows.length && month ? (
            <div className="pul-sum" data-testid="recurring-sum">
              <span>{RECURRING_MSG.footer(rows.length)}</span>
              <span>
                {RECURRING_MSG.footerTotal} <b className="num">{yen(rows.filter((r) => r.state === "active").reduce((a, r) => a + r.subtotal, 0))}</b> 円
              </span>
              <span>{RECURRING_MSG.footerMonth(mOf(month), counts("none"), counts("draft"), counts("issued"))}</span>
            </div>
          ) : null}
        </div>
      </div>
      {toastNode}
    </ListHead>
  );
}
